import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import config from "../config.ts";
import { Disc } from "./Circles.tsx";

const PANE_NAME = "circles";

// Draws all travel-time discs onto a single canvas. Rendering them as
// individual (react-)leaflet layers would put tens of thousands of objects
// on the map; one imperative canvas paints the same picture in tens of
// milliseconds.
const HeatCanvas = ({ discs }: { discs: Disc[] }) => {
  const map = useMap();

  // The canvas and its map listeners are created once; redraws read the
  // latest discs through these refs, so data changes only repaint pixels.
  const discsRef = useRef<Disc[]>(discs);
  const redrawRef = useRef<() => void>(() => {});

  useEffect(() => {
    let pane = map.getPane(PANE_NAME);
    if (!pane) {
      pane = map.createPane(PANE_NAME);
      pane.style.zIndex = "500";
      pane.style.opacity = String(config.opacity);
    }
    const canvas = L.DomUtil.create("canvas", "leaflet-layer", pane);

    const redraw = () => {
      const size = map.getSize();
      canvas.width = size.x;
      canvas.height = size.y;
      // Pin the canvas to the current viewport; leaflet translates the pane
      // while panning and we repaint on moveend.
      L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }
      ctx.clearRect(0, 0, size.x, size.y);

      // Metres per pixel at this zoom (web mercator), corrected per disc
      // latitude below.
      const zoomScale = 156543.03392 / Math.pow(2, map.getZoom());

      for (const disc of discsRef.current) {
        const point = map.latLngToContainerPoint([disc.lat, disc.lng]);
        const metersPerPixel =
          zoomScale * Math.cos((disc.lat * Math.PI) / 180);
        const radius = disc.radius / metersPerPixel;

        if (
          point.x + radius < 0 ||
          point.y + radius < 0 ||
          point.x - radius > size.x ||
          point.y - radius > size.y
        ) {
          continue;
        }

        ctx.fillStyle = disc.colour;
        ctx.beginPath();
        ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
        ctx.fill();
      }
    };
    redrawRef.current = redraw;

    // The stale picture can't cheaply track the basemap mid-zoom, so hide
    // it while zooming and repaint once the zoom settles.
    const hide = () => {
      canvas.style.visibility = "hidden";
    };
    const show = () => {
      redraw();
      canvas.style.visibility = "visible";
    };

    map.on("moveend viewreset resize", redraw);
    map.on("zoomstart", hide);
    map.on("zoomend", show);
    redraw();

    return () => {
      map.off("moveend viewreset resize", redraw);
      map.off("zoomstart", hide);
      map.off("zoomend", show);
      canvas.remove();
    };
  }, [map]);

  useEffect(() => {
    discsRef.current = discs;
    redrawRef.current();
  }, [discs]);

  return null;
};

export default HeatCanvas;
